import React, {
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import classnames from 'classnames';
import { MAP_BASE_URL, MAP_ROOT_AREA_THE_FIFTH_CITY } from 'mapping/constants';
import DistrictLabelLayer from 'components/Map/DistrictLabelLayer';
import { BaseProps } from 'components/Map/PixiMap/props';
import ZoomControl from 'components/Map/PixiMap/ZoomControl';
import ReactLeafletPixiOverlay from 'components/Map/ReactLeafletPixiOverlay';
import {
  areaToTooltipData,
  getMapDimensionsForSetting,
  getMinimumZoomThatFits,
  isDistrict,
  xy,
} from 'mapping';
import { ModalTooltip } from 'components/ModalTooltip/ModalTooltipContainer';
import L from 'leaflet';
import getCRSForSetting from 'mapping/getCRSForSetting';
import getMapZoomLimitsForSetting from 'mapping/getMapZoomLimitsForSetting';
import getMinimumZoomLevelForDestinations from 'mapping/getMinimumZoomLevelForDestinations';
import { ImageOverlay, Map as LeafletMap, Pane } from 'react-leaflet';
import { connect } from 'react-redux';
import getLabelledStateAwareAreas from 'selectors/map/getLabelledStateAwareAreas';
import { IAppState } from 'types/app';
import {
  IArea,
  IMappableSetting,
  IStateAwareArea,
  IStateAwareAreaWithHitbox
} from 'types/map';
import PlayerMarkers from '../PlayerMarkers';
import Hitbox from './Hitbox';

const SAFE_AREA_PADDING = 10;
const HITBOX_CLICK_ZOOM_LEVEL = 4;

type Props = BaseProps & ReturnType<typeof mapStateToProps>;

export function LondonPixiMap(props: Props) {
  const {
    areas,
    currentArea,
    initialCenter,
    initialZoom,
    onAreaClick,
    onAreaSelect,
    onClick,
    onMoveEnd,
    onZoomEnd,
    selectedArea,
    setting,
  } = props;

  const isPlayerInLondon = useMemo(() => {
    return setting.mapRootArea.areaKey === MAP_ROOT_AREA_THE_FIFTH_CITY;
  }, [setting.mapRootArea.areaKey]);

  const minimumZoomLevelForDestinations = getMinimumZoomLevelForDestinations(setting);

  const { height: mapHeight, width: mapWidth } = getMapDimensionsForSetting(setting as IMappableSetting);

  const mapRef = useRef<any>(null);
  const overlayRef = useRef<any>(null);

  const [isModalTooltipOpen, setIsModalTooltipOpen] = useState(false);
  const [tooltipData, setTooltipData] = useState({});
  const [zoomLevel, setZoomLevel] = useState(initialZoom);

  useEffect(() => {
    if (overlayRef.current !== null) {
      overlayRef.current!.redraw({ type: 'selectedArea', payload: { selectedArea } });
    }
  }, [selectedArea]);

  const zoomToDistrict = useCallback((area: IArea) => {
    // If we have no map, we can't zoom it; return
    if (mapRef.current === null) {
      return;
    }
    // If we have no label coordinates, we can't zoom anywhere; return
    if (area.labelX === undefined || area.labelY === undefined) {
      return;
    }
    const destination = xy(area.labelX, area.labelY);
    mapRef.current.leafletElement.setView(destination, HITBOX_CLICK_ZOOM_LEVEL, { animate: true });
  }, []);

  const handleAreaClick = useCallback(async (e: SyntheticEvent<Element, Event>, area: IArea) => {
    if (isDistrict(area) && zoomLevel < minimumZoomLevelForDestinations) {
      zoomToDistrict(area);
      return;
    }
    await onAreaClick(e, area);
  }, [
    minimumZoomLevelForDestinations,
    onAreaClick,
    zoomLevel,
    zoomToDistrict,
  ]);

  const handleHitboxClick = (area: IStateAwareArea) => {
    if (area.isDistrict && !area.isLit) {
      return;
    }
    if (mapRef.current === null) {
      return;
    }
    // We need to handle the hitbox click slightly differently, depending on zoom level.
    // If we are zoomed way out, then we zoom in. Otherwise, we move to the area.
    if (zoomLevel < minimumZoomLevelForDestinations) {
      return zoomToDistrict(area);
    }
    return onAreaClick(null, area);
  };

  const handleHitboxTap = useCallback((area) => {
    if (area.isDistrict && !area.isLit) {
      return;
    }

    if (area.isDistrict && zoomLevel < minimumZoomLevelForDestinations) {
      return zoomToDistrict(area);
    }

    onAreaSelect(area);

    const baseTooltipData = areaToTooltipData(area, currentArea, (e) => onAreaClick(e, area));
    setIsModalTooltipOpen(true);

    if (zoomLevel >= minimumZoomLevelForDestinations) {
      return setTooltipData(baseTooltipData);
    }
  }, [
    currentArea,
    minimumZoomLevelForDestinations,
    onAreaClick,
    onAreaSelect,
    zoomLevel,
    zoomToDistrict,
  ]);

  const handleRequestCloseModalTooltip = useCallback(() => {
    setIsModalTooltipOpen(false);
    onAreaSelect();
  }, [onAreaSelect]);

  const handleZoomEnd = useCallback((e) => {
    const zoomLevel = e.target.getZoom();
    setZoomLevel(zoomLevel);
    onZoomEnd(e);

    // Force-deselect if we've zoomed out beyond the threshold for showing destinations
    if (selectedArea?.isDistrict && zoomLevel < minimumZoomLevelForDestinations) {
      onAreaSelect();
    }
  }, [
    minimumZoomLevelForDestinations,
    onAreaSelect,
    onZoomEnd,
    selectedArea,
  ]);

  // Memoize
  const maxZoom = useMemo(() => getMapZoomLimitsForSetting(setting).max, [setting]);
  const minZoom = useMemo(() => Math.max(3, getMinimumZoomThatFits(window, setting as IMappableSetting)), [setting]);
  const nonDistrictAreas = areas.filter(a => !isDistrict(a) && a.shouldAppearOnMap);

  // noinspection PointlessArithmeticExpressionJS
  return (
    <LeafletMap
      ref={mapRef}
      attributionControl={false}
      maxBounds={[
        [0 - SAFE_AREA_PADDING, 0 + SAFE_AREA_PADDING],
        [-mapHeight + SAFE_AREA_PADDING, mapWidth - SAFE_AREA_PADDING],
      ]}
      bounceAtZoomLimits={false}
      maxBoundsViscosity={1}
      center={xy(initialCenter[0], initialCenter[1])}
      crs={getCRSForSetting(setting)}
      zoom={zoomLevel}
      minZoom={minZoom}
      maxZoom={maxZoom}
      zoomDelta={0.5}
      zoomSnap={0.5}
      onClick={onClick}
      onmoveend={onMoveEnd}
      onzoomend={handleZoomEnd}
      tms={false}
      zoomControl={false}
      className={classnames(isPlayerInLondon && 'leaflet-container--london')}
    >

      <Pane
        style={{ zIndex: 99 }} // Hide this pane if we can show basically anything else
      >
        {(
          <ImageOverlay
            url={`${MAP_BASE_URL}/fallback/fallback-dark.jpg`}
            bounds={L.latLngBounds(
              xy(0, 0),
              xy(mapWidth, -mapHeight),
            )}
          />
        )}
      </Pane>

      <ZoomControl
        setZoomLevel={setZoomLevel}
        zoomLevel={zoomLevel}
        zoomDelta={0.3}
      />

      <ReactLeafletPixiOverlay
        // @ts-ignore
        selectedArea={selectedArea}
      />

      {areas
        .filter(a => a.hasHitbox && a.shouldHaveHitboxAtZoomLevel(zoomLevel))
        .map(a => a as IStateAwareAreaWithHitbox)
        .map(area => (
          <Hitbox
            key={area.areaKey}
            area={area}
            onAreaSelect={onAreaSelect}
            onClick={handleHitboxClick}
            zoomLevel={zoomLevel}
          />
        ))}

      <DistrictLabelLayer
        areas={areas.filter(a => a.isDistrict && a.shouldAppearOnMap)}
        areaType="District"
        currentArea={currentArea}
        onAreaClick={handleAreaClick}
        onAreaSelect={onAreaSelect}
        onTapAtLowZoomLevel={handleHitboxTap}
        zoomLevel={zoomLevel}
      />

      <DistrictLabelLayer
        areas={nonDistrictAreas}
        areaType="Destination"
        currentArea={currentArea}
        minimumZoomLevel={minimumZoomLevelForDestinations}
        onAreaClick={onAreaClick}
        onAreaSelect={onAreaSelect}
        onTapAtLowZoomLevel={handleHitboxTap}
        zoomLevel={zoomLevel}
      />

      {/* <PlayerMarkerLayer /> */}
      <PlayerMarkers />

      <ModalTooltip
        modalIsOpen={isModalTooltipOpen}
        onRequestClose={handleRequestCloseModalTooltip}
        tooltipData={tooltipData}
        disableTouchEvents
      />
    </LeafletMap>
  );
}

const mapStateToProps = (state: IAppState) => ({
  areas: getLabelledStateAwareAreas(state),
  setting: state.map.setting! as IMappableSetting,
});

export default connect(mapStateToProps)(LondonPixiMap);